import React, { FC, useMemo, useState } from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts';
import { twMerge } from 'tailwind-merge';

import { AnimatedNumber } from '../../components/animated-number/index';
import { AppFormattedMessage } from '../../components/app-formatted-message/index';
import Button from '../../components/button/index';
import { useNavigate } from '../../hooks/index';
import { PageRoute } from '../../types/page.types';
import { Snapshot } from '../../types/snapshot.types';
import { getEquityPoolSplit, getPoolAllocation } from '../../utils/pool-allocation.util';
import { EmptyPositionGraph } from '../my-holdings/graphs/empty-position-graph';
import { PoolGraphVariant } from './variables';

export const colors = {
  [PoolGraphVariant.EQUITY]: {
    strokeColors: ['#D2a9FA', '#A6F4C5'],
    fillColors: ['#E9D8FA', '#D1FADF'],
    activeColors: ['#6717b6', '#039855'],
  },
  [PoolGraphVariant.GRANTED]: {
    strokeColors: ['#FEDF89', '#B4D1FE'],
    fillColors: ['#FEF0C7', '#D1E3FF'],
    activeColors: ['#DC6803', '#2565C8'],
  },
};

type PoolGraphProps = {
  isDesktop: boolean;
  isLoadingFinished: boolean;
  isTablet: boolean;
  snapshot?: Snapshot;
  variant: PoolGraphVariant;
};

export const PoolGraph: FC<PoolGraphProps> = ({
  snapshot,
  isDesktop,
  isTablet,
  variant,
  isLoadingFinished,
}) => {
  const [activeIndex, setActiveIndex] = useState<null | number>(null);

  const navigate = useNavigate(PageRoute.POOL_PLANS);
  const equityGraph = useMemo(() => {
    if (!snapshot) {
      return { data: [], totalShares: 0 };
    }
    const { equity, pool } = getEquityPoolSplit({
      dilutedShares: snapshot.dilutedShares,
      stakeholders: snapshot.stats?.stakeholders,
    });
    const totalShares = equity + pool;

    return {
      data: [
        {
          name: 'Equity',
          percentage: parseFloat(((equity * 100) / (totalShares || 1)).toFixed(2)),
        },
        {
          name: 'Pools',
          percentage: parseFloat(((pool * 100) / (totalShares || 1)).toFixed(2)),
        },
      ],
      totalShares,
    };
  }, [snapshot]);

  const grantedGraph = useMemo(() => {
    if (!snapshot) {
      return { data: [], totalShares: 0 };
    }
    const { granted, ungranted } = getPoolAllocation(snapshot.stats?.pools);
    const totalPoolShares = granted + ungranted;

    return {
      data: [
        {
          name: 'Ungranted Pool',
          percentage: parseFloat(((ungranted * 100) / (totalPoolShares || 1)).toFixed(2)),
        },
        {
          name: 'Granted Pool',
          percentage: parseFloat(((granted * 100) / (totalPoolShares || 1)).toFixed(2)),
        },
      ],
      totalShares: totalPoolShares,
    };
  }, [snapshot]);

  const handleMouseEnter = (index: number) => {
    setActiveIndex(index);
  };

  const handleMouseLeave = () => {
    setActiveIndex(null);
  };

  const { data, totalShares } = variant === PoolGraphVariant.EQUITY ? equityGraph : grantedGraph;

  // A company can have shares on the dashboard and still no pools at all — the granted
  // variant must fall back to the empty state on its own totals, not on the company's.
  const isGraphEmpty = !totalShares;
  const isMobile = !isDesktop && !isTablet;

  return (
    <div className={twMerge('centered relative flex h-full w-full')}>
      {isGraphEmpty && isLoadingFinished && (
        <div className="centered absolute z-1 flex flex-col gap-3">
          <span className="text-label-md font-medium text-gray-500">
            <AppFormattedMessage id="POOL_ALLOCATION_WILL_BE_DISPLAYED_HERE" />
          </span>
          <Button
            className="border border-gray-300 bg-gray-25 px-4 py-[10px] text-sm font-[450] text-gray-700"
            data-permission-required
            onClick={() => navigate()}
            styleType="DEFAULT_ROUNDED"
          >
            <AppFormattedMessage id="ADD_POOLS_PLANS" />
          </Button>
        </div>
      )}
      <div
        className={twMerge('flex w-full items-center justify-center gap-9', isMobile && 'flex-col')}
      >
        <div className={twMerge(isTablet ? 'size-[130px]' : 'size-[214px]')}>
          {isGraphEmpty ? (
            <EmptyPositionGraph />
          ) : (
            <ResponsiveContainer height="100%" width="100%">
              <PieChart>
                <Pie
                  cx="50%"
                  cy="50%"
                  data={data}
                  dataKey="percentage"
                  innerRadius="50%"
                  isAnimationActive={false}
                  outerRadius="100%"
                >
                  {data.map((entry, index) => (
                    <Cell
                      fill={
                        activeIndex === index
                          ? colors[variant].activeColors[
                              index % colors[variant].activeColors.length
                            ]
                          : colors[variant].fillColors[index % colors[variant].fillColors.length]
                      }
                      fillOpacity={1}
                      key={entry.name}
                      onMouseEnter={() => handleMouseEnter(index)}
                      onMouseLeave={handleMouseLeave}
                      stroke={
                        colors[variant].strokeColors[index % colors[variant].strokeColors.length]
                      }
                      style={{
                        transition: 'all 0.5s ease, stroke 0.5s ease',
                      }}
                    />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
        <div
          className={twMerge(
            'flex max-w-[108px] flex-col gap-6',
            isMobile && 'max-w-full flex-row justify-center gap-4',
          )}
        >
          {data.map((item, index) => (
            <div
              className={twMerge('flex w-[108px] flex-col gap-2', isMobile && 'items-center')}
              key={`${item.name}_${index}`}
            >
              {!isGraphEmpty ? (
                <span
                  className="text-label-md font-[600] text-gray-600"
                  style={{
                    color: activeIndex !== null && activeIndex !== index ? '#98A2B3' : '#344054',
                    transition: 'color 0.5s ease',
                  }}
                >
                  {item.name}
                </span>
              ) : (
                <div className="h-2 w-full bg-gray-50 blur-md" />
              )}

              {!isGraphEmpty ? (
                <>
                  {' '}
                  <div className="flex items-center gap-2">
                    <div
                      className="h-[5px] w-3 rounded"
                      style={{
                        backgroundColor:
                          activeIndex === index
                            ? colors[variant].activeColors[
                                index % colors[variant].activeColors.length
                              ]
                            : colors[variant].fillColors[index % colors[variant].fillColors.length],
                        border:
                          activeIndex === index
                            ? `1px solid ${colors[variant].activeColors[index % colors[variant].activeColors.length]}`
                            : `1px solid ${colors[variant].fillColors[index % colors[variant].fillColors.length]}`,
                        transition: 'background-color 0.5s ease, border 0.5s ease',
                      }}
                    />
                    <span
                      className="whitespace-nowrap text-sm font-[450] text-gray-700"
                      style={{
                        color:
                          activeIndex !== null
                            ? activeIndex === index
                              ? colors[variant].activeColors[
                                  index % colors[variant].activeColors.length
                                ]
                              : '#98A2B3'
                            : '#344054',
                        transition: 'color 0.5s ease',
                      }}
                    >
                      <AnimatedNumber maximumFractionDigits={2} value={item.percentage} />%
                    </span>
                  </div>
                </>
              ) : (
                <div className="h-2 w-full bg-gray-700 blur-md" />
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
